/**
 * Connection point between lab implementation (`src/labs/<lab-id>/`) and the app.
 * Branching story data is loaded from `src/data/labs/<lab-id>/workbook-content.js` (story + `labManifest`).
 *
 * Registration is intentionally glob-based so new labs are picked up without editing a central
 * manifest—reducing merge conflicts and the chance a lab is implemented but never wired in.
 * Story content stays in `src/data/labs` so narrative data can evolve independently of lab module
 * code and so workbook loading can remain async while lab module metadata is fixed at build time.
 */

/** @typedef {import("./types/labRegistry.types.js").LabTourStepBuilders} LabTourStepBuilders */
/** @typedef {import("./types/labRegistry.types.js").LabRegistryPluginOptions} LabRegistryPluginOptions */
/** @typedef {import("./types/labRegistry.types.js").LabAttemptCSVHelpers} LabAttemptCSVHelpers */

// Lazy glob: one async chunk per lab; only the active lab’s module is fetched at runtime.
// Vite still discovers every matching path at build time.
const labModuleLoaders = import.meta.glob("./labs/*/module.js");

if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
  /**
   * Dev-only eager glob for folder/LAB_ID/manifest alignment (audit 2.3). Production builds omit
   * this block; runtime still uses lazy loaders above so deploy bundles do not pull every lab into
   * the initial graph.
   */
  const labModuleGlobForAudit = import.meta.glob("./labs/*/module.js", {
    eager: true
  });
  for (const [path, mod] of Object.entries(labModuleGlobForAudit)) {
    if (!mod.default) {
      console.warn(
        `[labRegistry] Skipping "${path}": expected default export (LabModule).`
      );
    }
  }
  const workbookModules = import.meta.glob(
    "./data/labs/*/workbook-content.js",
    {
      eager: true
    }
  );
  assertLabIdAlignment(labModuleGlobForAudit, workbookModules);
}

/**
 * Folder names are the canonical lab id in the repo; mismatches between folder, `LAB_ID`, and
 * `labManifest.id` cause subtle runtime bugs (wrong story loaded, wrong unlock passage). We fail
 * fast in dev rather than at deploy or in the browser. Orphan data folders only warn so unfinished
 * work on a second lab does not break local dev for the primary lab.
 *
 * @param {Record<string, { default?: unknown, LAB_ID?: string }>} labModuleGlobMap
 * @param {Record<string, { labManifest?: { id: string } }>} workbooks
 */
export function assertLabIdAlignment(labModuleGlobMap, workbooks) {
  const labModuleFolders = new Set();
  for (const [path, mod] of Object.entries(labModuleGlobMap)) {
    const folderMatch = path.match(/^\.\/labs\/([^/]+)\/module\.js$/);
    if (!folderMatch) continue;
    const folder = folderMatch[1];
    if (!mod.default) continue;
    const labModule = mod.default;
    if (labModule.LAB_ID !== folder) {
      throw new Error(
        `[labRegistry] LAB_ID mismatch: folder "${folder}" but lab module has LAB_ID "${labModule.LAB_ID}" (${path}).`
      );
    }
    labModuleFolders.add(folder);
  }

  const workbookFolders = new Set();
  for (const [path, mod] of Object.entries(workbooks)) {
    const folderMatch = path.match(
      /data\/labs\/([^/]+)\/workbook-content\.js$/
    );
    if (!folderMatch) continue;
    const folder = folderMatch[1];
    if (!mod.labManifest) {
      throw new Error(`[labRegistry] Missing labManifest export in ${path}`);
    }
    if (mod.labManifest.id !== folder) {
      throw new Error(
        `[labRegistry] labManifest.id mismatch: folder "${folder}" but labManifest.id is "${mod.labManifest.id}" (${path}).`
      );
    }
    workbookFolders.add(folder);
  }

  for (const id of labModuleFolders) {
    if (!workbookFolders.has(id)) {
      throw new Error(
        `[labRegistry] No workbook at src/data/labs/${id}/workbook-content.js for lab module in src/labs/${id}/.`
      );
    }
  }

  for (const id of workbookFolders) {
    if (!labModuleFolders.has(id)) {
      console.warn(
        `[labRegistry] Orphan data lab folder "${id}": no matching src/labs/${id}/module.js (discovered lab modules only).`
      );
    }
  }
}

/** Resolved active lab module after {@link initActiveLab}; null if missing or failed. */
let activeLabModule = null;

/**
 * Loads `src/labs/<VITE_LAB_ENV>/module.js` (async chunk). Call once before `createLab` / mount.
 *
 * @returns {Promise<void>}
 */
export async function initActiveLab() {
  const labId = import.meta.env.VITE_LAB_ENV;
  const path = `./labs/${labId}/module.js`;
  const load = labModuleLoaders[path];
  if (!load) {
    activeLabModule = null;
    return;
  }
  try {
    const mod = await load();
    activeLabModule = mod.default ?? null;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error(`[labRegistry] Failed to load lab module at ${path}`, err);
    }
    activeLabModule = null;
  }
}

/** Build-time active lab id; must match `src/data/labs/<id>/` and `src/labs/<id>/`. */
export const ACTIVE_LAB_ID = import.meta.env.VITE_LAB_ENV;

/**
 * Maps `$lab` state to `{ measurements, solutions }` for the current attempt (active lab only).
 *
 * @param {object} labService - Vue lab service from `LabVuePlugin`
 * @returns {{ measurements: unknown[], solutions: object }}
 */
export function getAttemptPayload(labService) {
  if (!activeLabModule?.getAttemptPayload) {
    return { measurements: [], solutions: {} };
  }
  return activeLabModule.getAttemptPayload(labService);
}

/**
 * @param {string} labId
 * @returns {(() => Promise<import("vue").Component>) | undefined}
 */
export function getLabAreaLoader(labId) {
  // Guard even though callers usually pass `VITE_LAB_ENV`: prevents rendering another lab’s UI if
  // ids ever diverge (bad env, future multi-lab routing experiments).
  if (!activeLabModule || labId !== activeLabModule.LAB_ID) return undefined;
  return activeLabModule.labAreaLoader;
}

/**
 * Async loader for the Lab Attempts review chart (measurements summary), when the lab provides one.
 * @param {string} labId
 * @returns {(() => Promise<import("vue").Component>) | undefined}
 */
export function getReviewComponentLoader(labId) {
  if (!activeLabModule || labId !== activeLabModule.LAB_ID) return undefined;
  return activeLabModule.reviewComponentLoader;
}

/**
 * Single-attempt CSV for download. Uses the active lab’s `generateAttemptCSV` when defined.
 *
 * @param {object} attempt
 * @param {number} attemptNumber
 * @param {LabAttemptCSVHelpers} helpers
 * @returns {string}
 */
export function exportLabAttemptCSV(attempt, attemptNumber, helpers) {
  if (activeLabModule?.generateAttemptCSV) {
    return activeLabModule.generateAttemptCSV(attempt, attemptNumber, helpers);
  }
  // Labs without a custom formatter still get a usable export for instructors and support.
  const rows = [];
  rows.push(["Lab Attempt", attemptNumber]);
  rows.push(["Started", helpers.formatDateTime(attempt.timestamp)]);
  rows.push(["Status", attempt.isComplete ? "Completed" : "In Progress"]);
  if (attempt.completedAt) {
    rows.push(["Completed", helpers.formatDateTime(attempt.completedAt)]);
  }
  rows.push([]);
  rows.push([
    "Measurements recorded",
    String((attempt.measurements || []).length)
  ]);
  if (!(attempt.measurements || []).length) {
    rows.push(["Note", "No measurements recorded"]);
  }
  return rows.map((row) => row.join(",")).join("\n");
}

/**
 * Labs framework plugins for the active deployment.
 * @param {import("./types/labRegistry.types.js").LabRegistryPluginOptions} options
 */
export function getExperimentPlugins(options) {
  // Empty array when misconfigured: `createLab` still runs; the app should not crash solely because
  // `VITE_LAB_ENV` points at a missing lab module (other code paths may surface the error).
  if (!activeLabModule) return [];
  return activeLabModule.getExperimentPlugins(options);
}

// Shared reference so consumers that read tour builders repeatedly don’t allocate new noop objects.
const EMPTY_TOUR_BUILDERS = {
  buildDesktopLabSteps: () => [],
  buildMobileLabSteps: () => []
};

/**
 * Intro.js step builders for the lab, or empty steps when none are registered.
 * @param {string} labId
 * @returns {LabTourStepBuilders}
 */
export function getLabTourStepBuilders(labId) {
  if (!activeLabModule || labId !== activeLabModule.LAB_ID) {
    return EMPTY_TOUR_BUILDERS;
  }
  return activeLabModule.tourStepBuilders;
}

/**
 * Maps lab id → passage id → (measurements) => null | { title, body } (active lab only).
 * @returns {Record<string, Record<string, (measurements: unknown[]) => { title: string, body: string } | null>>}
 */
export function getStepExitChecks() {
  return activeLabModule?.STEP_EXIT_CHECKS ?? {};
}

/**
 * @param {{ labId: string, passageId: string, measurements: unknown[] }} args
 * @returns {{ title: string, body: string } | null}
 */
export function getExitWarning(args) {
  if (!activeLabModule) return null;
  return activeLabModule.getExitWarning(args);
}

/**
 * Workbook entry: default story export + `labManifest`.
 * @param {string} labId
 */
export async function loadLabEntryModule(labId) {
  // Dynamic import keeps the workbook chunk separate from the registry and matches how story data is
  // loaded on demand when the activity view initializes (large narratives are not inlined here).
  const content = await import(`@/data/labs/${labId}/workbook-content.js`);
  return { default: content.default, labManifest: content.labManifest };
}
